import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { ThemeDrawer, DrawerCloseEvent } from '@theme/theme-drawer';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';
import { SlideshowSelectEvent } from '@theme/events';

// How long the skeleton will wait for the panel's photo before giving up on it. A ceiling
// and not a duration: the wait almost always ends on the image itself, and this only decides
// how bad a slow connection is allowed to get.
const PANEL_IMAGE_DECODE_TIMEOUT = 1000;

/**
 * The section the panel is filled from, rendered on its own by the Section Rendering API
 * (sections/quick-add-product.liquid).
 *
 * This used to fetch the product page itself and keep the one element out of it the panel
 * morphs in, which meant paying for the header, the footer, the recommendation strips and
 * the `<head>` on every tap - about 3.1 MB of HTML and ~900 ms of Liquid for a column of
 * roughly 40 KB. A section asked for by name is the theme's established way out of that;
 * `section-rendering-product-card` and `shop-the-look-product` are fetched the same way.
 *
 * By name rather than by the id the product template gives its own product section: that
 * id belongs to the theme, so duplicating the theme for staging or production changes it,
 * and there is nothing on a collection page that knows what it is.
 */
const QUICK_ADD_SECTION_ID = 'quick-add-product';

/**
 * Fetches the quick-add column for a product.
 *
 * @param {string} productUrl - The product's own URL. Its `variant`, if it carries one,
 *   is what decides the size the column comes back on, so it is kept.
 * @param {AbortSignal} signal - Aborts with the request it was made for.
 * @returns {Promise<Document | null>} `null` when the request was aborted, which on this
 *   panel only happens because a newer one replaced it.
 */
async function fetchQuickAddSection(productUrl, signal) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('section_id', QUICK_ADD_SECTION_ID);

  try {
    const response = await fetch(url, { signal });

    if (!response.ok) throw new Error(`Failed to fetch quick add section: HTTP error ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  } catch (error) {
    if (error.name === 'AbortError') return null;

    throw error;
  }
}

/**
 * Puts a fetched column into the panel.
 *
 * @param {Element} productGrid - The column to show. Mutated, so hand it a clone if it is
 *   being kept.
 * @returns {Element | null} The panel's content element, once filled.
 */
function fillPanel(productGrid) {
  const modalContent = document.getElementById('quick-add-modal-content');

  if (!productGrid || !modalContent) return null;

  // A subtree marked `data-skip-subtree-update` survives a morph untouched. That is right
  // where the marker was written for - the product page, where a morph only ever swaps one
  // variant of a product for another, and rebuilding those rows would throw away state the
  // components applied to them - and wrong here, because this panel is a single element that
  // every card in the grid opens. The product under it changes, and the skipped rows would
  // stay on the one before: the colour swatches of whatever was opened first.
  //
  // Dropped rather than unmarked: with no old node to pair with, the morph inserts the
  // incoming row instead of skipping it, and the row it inserts carries the marker itself, so
  // variant changes inside the panel go on being protected by it.
  const incomingProductId = productGrid.querySelector('variant-picker')?.dataset.productId;
  const currentProductId = modalContent.querySelector('variant-picker')?.dataset.productId;

  if (incomingProductId && currentProductId && incomingProductId !== currentProductId) {
    for (const stale of modalContent.querySelectorAll('[data-skip-subtree-update]')) {
      stale.remove();
    }

    // The gallery goes with them, so the incoming one is inserted rather than patched onto
    // the outgoing one's `<img>` elements. Two things follow from those elements being new.
    // They have painted nothing yet, so there is no previous product's photo to sit under
    // this product's name while the first one loads. And `loading="lazy"` means what it says
    // again: an `<img>` that has already loaded once fetches immediately when a morph gives
    // it a new `src`, which had the panel pulling most of a gallery on every open once the
    // shopper had swiped through one.
    //
    // Only when the product changes. A variant of the same product keeps its gallery, which
    // is what stops a colour's photos being thrown away and fetched again on a size change.
    modalContent.querySelector('.product-information__media')?.remove();
  }

  // Sync the view-event-payload attribute and morph children into the modal's product-component
  modalContent.setAttribute('view-event-payload', productGrid.getAttribute('view-event-payload') || '');

  morph(modalContent, productGrid);

  return modalContent;
}

/**
 * Holds the skeleton until the panel's photo has actually decoded.
 *
 * The morph gives the `<img>` a new `src`, but the browser goes on painting the picture it
 * has already decoded until the new one is ready. Taking the skeleton away the moment the
 * DOM is correct therefore shows the previous product's photo under the new product's name
 * and price - the stale frame this waits out.
 *
 * Only the first image, which is the one the gallery opens on: the rest are off screen and
 * would hold the panel for pictures nobody is looking at.
 */
async function awaitPanelImage() {
  const image = document.querySelector('#quick-add-modal-content .product-information__media img');

  if (!(image instanceof HTMLImageElement)) return;
  if (image.complete && image.naturalWidth > 0) return;

  await Promise.race([
    // Rejects if the image fails, or if its `src` changes again while it decodes. Neither
    // is worth holding the panel for, and both end the wait the same way.
    image.decode().catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, PANEL_IMAGE_DECODE_TIMEOUT)),
  ]);
}

/**
 * Sends the panel back to the top of whichever of its three scrollers is live - desktop
 * scrolls each pane on its own, mobile scrolls the stacked content as a whole. Resetting
 * all three is cheaper than asking which one it is.
 *
 * @param {Element} drawer - The panel.
 */
function resetPanelScroll(drawer) {
  drawer.querySelector('#quick-add-modal-content')?.scrollTo({ top: 0, behavior: 'instant' });
  drawer.querySelector('.product-details')?.scrollTo({ top: 0, behavior: 'instant' });
  drawer.querySelector('.product-information__media')?.scrollTo({ top: 0, behavior: 'instant' });

  // And the gallery back to its first photo, which the three above cannot do: the carousel
  // scrolls sideways, and it is the one scroller in the panel the morph hands straight back.
  //
  // `slideshow.js` re-selects a slide of its own accord only when its `initial-slide`
  // attribute *changes*, and every product's gallery opens on the same one — so the element
  // came through the morph still parked wherever the last shopper had swiped it. The next
  // product then opened on its fourth photo with the dots reading "1", the skeleton lifted on
  // a slide nobody was looking at, and the panel felt stuck until a control was tapped, which
  // is the one route that goes through `select()` and puts the two back in step.
  //
  // In a microtask, as `attributeChangedCallback` does it there, so the morph has settled
  // before the component is asked to measure anything.
  queueMicrotask(() => {
    const slideshow = drawer.querySelector('.product-information__media slideshow-component');

    if (slideshow && 'select' in slideshow && typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }

    preloadNextPanelSlide(drawer);
  });
}

/**
 * Takes `loading="lazy"` off the photo after the one on screen.
 *
 * The panel's gallery loads lazily (sections/quick-add-product.liquid), which is what keeps a
 * phone from fetching every photo a product has on every open. The cost of that is the next
 * one not starting until it is swiped to, and on Safari the gap between the swipe and the
 * picture arriving is long enough to watch. So the gallery runs one ahead: whichever slide is
 * showing, the one after it is already on its way. `product-card.js` keeps its own carousel
 * ahead the same way, for the same reason.
 *
 * @param {Element} drawer - The panel.
 */
function preloadNextPanelSlide(drawer) {
  const slideshow = /** @type {any} */ (drawer.querySelector('.product-information__media slideshow-component'));
  const next = slideshow?.slides?.[(slideshow.current ?? 0) + 1];
  const image = next?.querySelector('img[loading="lazy"]');

  if (!(image instanceof HTMLImageElement)) return;

  // Fetched through an image of our own rather than by taking `loading` off this one. The
  // panel hands the same `<img>` elements to the next product it shows, and an element left
  // eager starts fetching the moment the morph gives it a `src` - so un-lazying slides as the
  // shopper swiped left the *following* open loading everything those elements had been
  // promoted to, and the saving went with it. A detached image warms the same cache entry and
  // leaves the DOM exactly as the section rendered it.
  const preload = new Image();

  preload.sizes = image.sizes;
  preload.srcset = image.srcset;
  preload.src = image.src;
}

/**
 * One load of a product's quick-add column.
 *
 * @typedef {object} QuickAddLoad
 * @property {Promise<Element | null | undefined>} promise - The column as fetched and never
 *   mutated; `null` when the response had no column in it, `undefined` when it was aborted.
 * @property {AbortController} controller - Aborts the request while it is in flight.
 * @property {boolean} claimed - A click is waiting on it, so no prefetch may abort it.
 * @property {boolean} settled - The request is over; the entry is the cache from then on.
 */

export class QuickAddComponent extends Component {
  /**
   * This card's quick-add columns by product URL, kept as the promise of one: a settled entry
   * is the cache a repeat open reads, a pending one is a prefetch the click picks up instead
   * of asking the server to render the same column a second time.
   * @type {Map<string, QuickAddLoad>}
   */
  #loads = new Map();
  /**
   * The panel session this card opened, while it is still the one filling the panel. Read on
   * disconnect, where it decides whether the fetch in flight is still wanted.
   * @type {number | null}
   */
  #panelSession = null;
  /**
   * Recreated on every `connectedCallback` rather than once as a field initializer — an
   * `AbortController` is single-use, and this component can connect more than once in its
   * lifetime (e.g. blocks/_collection-reel-image.liquid's hover card is physically moved
   * between the tile and the synced carousel on every breakpoint crossing, which
   * disconnects and reconnects it). Reusing an already-aborted controller would make
   * `addEventListener(..., { signal })` below a silent no-op on every reconnect after the
   * first.
   * @type {AbortController | null}
   */
  #cartUpdateAbortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    // The ancestor does not exist outside a product grid — snippets/quick-add.liquid
    // always has the `product` it was rendered with, so it stamps that product's URL
    // here as a fallback for any other context (e.g. blocks/_product-editorial-card.liquid).
    const href = productLink?.href || this.dataset.productUrl;

    if (!href) return '';

    const url = new URL(href, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#cartUpdateAbortController = new AbortController();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);

    // Not while this card is still the one filling the panel. Aborting on disconnect is right
    // for a card that scrolls away or is re-rendered with nothing waiting on it, but the panel
    // this request fills belongs to the page and outlives the card: a results list that
    // re-renders under an open quick add - which is what the Algolia panel does on mobile -
    // would otherwise cancel the very fetch the shopper is watching the skeleton for, and
    // nothing would ever take the skeleton away. The session check before the morph still
    // decides whether the answer is wanted by the time it arrives.
    if (!this.#ownsOpenPanel()) this.#abortPendingLoads();

    this.#cartUpdateAbortController?.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected.
   *
   * Correlates the event to this exact product/instance the same way
   * product-price.js, product-sku.js and product-inventory.js do — via the
   * resolved `detail.productId`, not DOM ancestry. A `<product-card>`-based
   * guard degenerates to `null !== null` (always passes) in any context
   * where quick-add isn't rendered inside a `<product-card>` — e.g.
   * blocks/_product-editorial-card.liquid — letting an unrelated product's
   * selection (including the shared quick-add modal's variant picker, which
   * lives outside every `<product-card>` too) restamp this button.
   *
   * Also fixes the underlying looseness this guard bug was masking:
   * `variants_count == 1 or (options.size == 1 and selected_variant)` is the
   * Liquid rule (snippets/quick-add.liquid) — `variant` here is that
   * "selected_variant" half; a multi-option product (`productOptionsCount`
   * other than `'1'`) always stays 'choose' regardless, matching Liquid.
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    event.promise.then(({ variant, detail }) => {
      if (!detail) return;

      const { newProduct, productId } = detail;
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const productOptionsCount = this.dataset.productOptionsCount;
      const quickAddButton = productOptionsCount === '1' && variant ? 'add' : 'choose';
      this.setAttribute('data-quick-add-button', quickAddButton);
    });
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#loads.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * The one panel every card's quick add fills, shared between them and rendered
   * once from layout/theme.liquid.
   *
   * @returns {QuickAddDrawer | null}
   */
  get #drawer() {
    const element = document.getElementById('quick-add-dialog');

    return element instanceof QuickAddDrawer ? element : null;
  }

  /**
   * Whether the panel is still open under the session this card opened. Looked up through the
   * document rather than held as a reference, so it answers after the card has been detached,
   * which is the case it exists for. A close bumps the session, so a dismissed panel is nobody's.
   */
  #ownsOpenPanel() {
    const drawer = this.#drawer;

    return drawer != null && this.#panelSession != null && drawer.loadingSession === this.#panelSession;
  }

  /**
   * Handles quick add button click.
   *
   * The panel opens on the click and the product page is fetched behind it, not the
   * other way round: a request with no feedback on it is a button that reads as
   * broken for as long as the network takes. Until the column lands the drawer shows
   * its skeleton (snippets/skeleton-quick-add.liquid), with the product's name
   * already on mobile's header row — the card that was tapped knows it.
   *
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const drawer = this.#drawer;
    if (!drawer) return;

    const currentUrl = this.productPageUrl;
    const session = this.#openQuickAddModal(drawer);

    this.#panelSession = session;

    /** @type {Element | null | undefined} */
    let productGrid;

    try {
      // Already settled when this card was opened before, in flight when the hover prefetch
      // got there first, started here otherwise. Claimed in every case, so nothing the
      // pointer or a variant change does from now on can abort it.
      productGrid = await this.#load(currentUrl, { claim: true });
    } catch (error) {
      console.warn('[quick-add] Could not load the product page:', error);
      this.#fallBackToProductPage(drawer, session, currentUrl);
      return;
    }

    // Aborted. A claimed load only is when the card is detached with the panel no longer
    // its own, so there is nobody left waiting on it.
    if (productGrid === undefined) return;

    // Another product's quick add, or this panel being dismissed, happened while the
    // fetch was in flight: morphing now would swap the shopper's panel out from
    // under them.
    if (drawer.loadingSession !== session) return;

    if (!productGrid) {
      this.#fallBackToProductPage(drawer, session, currentUrl);
      return;
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);
    await awaitPanelImage();

    // The wait above is one more chance for the shopper to move on. A newer open owns the
    // panel by now and will take the skeleton away itself.
    if (drawer.loadingSession !== session) return;

    drawer.finishLoading();
    // After the skeleton comes off, in the same task: both need a layout, and this way they
    // share one. Called straight after the morph it forced a layout of its own (120-180ms
    // on a throttled phone) that the skeleton coming off then threw away. Nothing paints in
    // between, so the shopper still never sees the old scroll position.
    resetPanelScroll(drawer);
  };

  /**
   * The panel is open and there is nothing to put in it — the fetch failed, or the
   * page it returned had no product column in it. Hand the shopper the product page
   * instead: it is the destination the CTA's own `href` carries and what the click
   * would have done with no JS at all, which beats both a panel stuck on its
   * skeleton and one that closes itself and leaves the tap unanswered.
   *
   * @param {QuickAddDrawer} drawer - The panel this ran for.
   * @param {number} session - The open it ran for; a newer one owns the panel now.
   * @param {string} url - The product page to fall back to.
   */
  #fallBackToProductPage(drawer, session, url) {
    if (drawer.loadingSession !== session || !url) return;

    drawer.close();
    window.location.assign(url);
  }

  /** @param {QuickAddDrawer} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DrawerCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * Opens the panel on this product: the name the card already knows on mobile's
   * header row, the skeleton over the pane the fetch is about to fill, and the
   * trigger held visible until the drawer closes again.
   *
   * @param {QuickAddDrawer} dialogComponent - The shared panel.
   * @returns {number} The session this open holds the panel under.
   */
  #openQuickAddModal(dialogComponent) {
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    const session = dialogComponent.startLoading(this.dataset.productCardTitle ?? '');

    dialogComponent.open();

    return session;
  }

  #closeQuickAddModal = () => {
    this.#drawer?.close();
  };

  /**
   * Starts loading this card's column ahead of a click that may never come: a pointer
   * resting on the card, keyboard focus reaching it, a variant change re-pointing it. The
   * click then picks up the same request rather than starting another one. A failure is
   * left for the click to run into, where it has a fallback.
   */
  prefetch() {
    this.#load(this.productPageUrl).catch(() => {});
  }

  /**
   * The load for `url`, reused when there is one and started otherwise.
   *
   * Starting one aborts this card's other prefetches still in flight: the card only ever
   * points at one product URL, so a prefetch for another is one the shopper has moved on
   * from. A claimed load is left alone, since a panel is waiting on it.
   *
   * @param {string} url - The product URL, as `productPageUrl` builds it.
   * @param {{ claim?: boolean }} [options] - `claim` for the click that will show the result.
   * @returns {Promise<Element | null | undefined>}
   */
  #load(url, { claim = false } = {}) {
    if (!url) return Promise.resolve(null);

    const existing = this.#loads.get(url);
    if (existing) {
      if (claim) existing.claimed = true;
      return existing.promise;
    }

    for (const [otherUrl, other] of this.#loads) {
      if (other.settled || other.claimed) continue;

      other.controller.abort();
      this.#loads.delete(otherUrl);
    }

    const controller = new AbortController();

    /** @type {QuickAddLoad} */
    const load = { controller, claimed: claim, settled: false, promise: Promise.resolve(undefined) };

    load.promise = fetchQuickAddSection(url, controller.signal)
      .then((html) => {
        if (!html) return undefined;

        const gridElement = html.querySelector('[data-product-grid-content]');

        return gridElement ? /** @type {Element} */ (gridElement.cloneNode(true)) : null;
      })
      .then(
        (productGrid) => {
          // Only a column is worth keeping. An abort or an empty response must not stay
          // cached, or every later open of this card would read it back.
          if (!productGrid) this.#forget(url, load);
          return productGrid;
        },
        (error) => {
          this.#forget(url, load);
          throw error;
        },
      )
      .finally(() => {
        load.settled = true;
      });

    this.#loads.set(url, load);

    return load.promise;
  }

  /**
   * Drops `load` from the cache, unless a newer load for the same URL has replaced it.
   * @param {string} url
   * @param {QuickAddLoad} load
   */
  #forget(url, load) {
    if (this.#loads.get(url) === load) this.#loads.delete(url);
  }

  /**
   * Aborts every load of this card still in flight, claimed or not.
   */
  #abortPendingLoads() {
    for (const [url, load] of this.#loads) {
      if (load.settled) continue;

      load.controller.abort();
      this.#loads.delete(url);
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    // No mobile-only restructuring: the drawer is one layout at both breakpoints
    // (the panes stack, they do not change). Rebuilding the column here used to be
    // how the old centred modal got a compact mobile view, and it also meant the
    // DOM was wrong for whichever breakpoint the shopper resized into.
    const modalContent = fillPanel(productGrid);

    if (modalContent) this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDrawer extends ThemeDrawer {
  #abortController = new AbortController();

  /**
   * Aborts a colour still loading when the shopper picks another one.
   * @type {AbortController | null}
   */
  #siblingAbortController = null;

  /**
   * Bumped on every open and every close, so a fetch that resolves late can tell
   * whether the panel it set out to fill is still the one it was opened for. A
   * counter rather than a flag: two cards' quick adds in a row, or two clicks on
   * one, are ordinary things for a shopper to do.
   *
   * @type {number}
   */
  #loadingSession = 0;

  /** @returns {number} The session the panel is currently held under. */
  get loadingSession() {
    return this.#loadingSession;
  }

  /**
   * Puts the panel in the state it opens in: the product's name on mobile's header
   * row — all the tapped card knows of the product, and enough to not open on a
   * placeholder — and `aria-busy`, which is what shows the skeleton
   * (snippets/skeleton-quick-add.liquid) over the pane the fetch is going to fill.
   *
   * @param {string} [title] - The product's display title, from the card that was clicked.
   *   Left out when the panel is already open on the product's own name and only its colour
   *   is changing, which is a name the row already has.
   * @returns {number} The session this open holds the panel under.
   */
  startLoading(title) {
    const { panel, headerTitle } = this.refs;

    if (title != null && headerTitle instanceof HTMLElement) headerTitle.textContent = title;
    panel.setAttribute('aria-busy', 'true');

    return ++this.#loadingSession;
  }

  /**
   * Takes the skeleton away, once the fetched column is in the panel — and puts that
   * column's own title on the header row over the one the card opened with. They are
   * the same string in the ordinary case; where they are not is a combined listing,
   * whose card swaps products under the shopper as they pick a swatch. The
   * `productSelect` event that tells the card so carries an id and a url and no name
   * (`newProduct` in assets/global.d.ts), so the column is the only place the name of
   * the product actually being shown can be read.
   */
  finishLoading() {
    const { panel, headerTitle } = this.refs;
    const title = panel.querySelector('[ref="productTitleLink"]')?.textContent?.trim();

    if (headerTitle instanceof HTMLElement && title) headerTitle.textContent = title;

    panel.removeAttribute('aria-busy');
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DrawerCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleSiblingClick, { signal: this.#abortController.signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#siblingAbortController?.abort();
    this.removeEventListener(DrawerCloseEvent.eventName, this.#handleDialogClose);
  }

  /** Keeps the gallery one photo ahead of the shopper - see `preloadNextPanelSlide`. */
  #handleSlideSelect = () => {
    preloadNextPanelSlide(this);
  };

  /**
   * A colour picked inside the panel.
   *
   * Sebago's colours are separate products rather than a variant option, so the swatches
   * are links to those products (snippets/product-siblings-swatches.liquid) and a click
   * would leave for the product page - which is the right thing from a card, and the wrong
   * thing from a panel the shopper opened to stay where they were. So the panel loads the
   * colour in place instead, from the same section it was filled from.
   *
   * Only the swatch's own click is taken: everything else in the panel - the size chips,
   * Add to cart, the gallery - is left alone. `stopPropagation` as well as
   * `preventDefault`, because the click would otherwise reach the drawer's own
   * outside-click handling. sections/shop-the-look-product.liquid's drawer does the same
   * for its swatches (assets/shop-the-look-products.js).
   *
   * @param {Event} event
   */
  #handleSiblingClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const swatch = target.closest('[data-sibling-swatch]');

    if (!swatch || !this.contains(swatch) || swatch.classList.contains('is-selected')) return;

    event.preventDefault();
    event.stopPropagation();

    // The live `href` rather than `data-product-url`: product-siblings-swatches.js points it
    // at the size the shopper has already picked, and the section honours its `?variant=`.
    this.#loadSibling(swatch.getAttribute('href') || swatch.getAttribute('data-product-url'));
  };

  /**
   * Swaps the product in the open panel.
   *
   * The panel is already open, so none of the opening happens again - no drawer to show, no
   * trigger to hold visible, no scroll to lock. What it borrows from an open is the
   * skeleton, which `startLoading` raises, and the session that skeleton is raised under,
   * so a colour that lands after the shopper has moved on is dropped rather than shown. The
   * header row keeps the name it has: a sibling is the same product in another colour.
   *
   * @param {string | null} url - The colour's product URL.
   */
  async #loadSibling(url) {
    if (!url) return;

    this.#siblingAbortController?.abort();
    this.#siblingAbortController = new AbortController();

    const session = this.startLoading();

    /** @type {Document | null} */
    let html = null;

    try {
      html = await fetchQuickAddSection(url, this.#siblingAbortController.signal);
    } catch (error) {
      console.warn('[quick-add] Could not load the colour:', error);
    }

    // Aborted, so a newer colour owns the panel and will take the skeleton away itself.
    if (!html) {
      if (this.loadingSession === session) this.#failSibling(url);
      return;
    }

    if (this.loadingSession !== session) return;

    const productGrid = html.querySelector('[data-product-grid-content]');

    if (!productGrid) {
      this.#failSibling(url);
      return;
    }

    fillPanel(productGrid);
    await awaitPanelImage();

    if (this.loadingSession !== session) return;

    this.finishLoading();
    // After the skeleton, sharing its layout - see the product path above.
    resetPanelScroll(this);
  }

  /**
   * The colour could not be loaded. The panel is open on the product before it, which is
   * still a true panel to be looking at, so the skeleton comes off and the shopper is left
   * where they were rather than sent away mid-browse - the swatch is a link, and trying
   * again or following it are both still open to them.
   *
   * @param {string} url - The colour that failed, for the warning.
   */
  #failSibling(url) {
    console.warn('[quick-add] Could not load the colour:', url);
    this.finishLoading();
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.close();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-more-details a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-more-details a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    // A dismissed panel is nobody's to fill: a fetch still in flight for it has to
    // find out it lost, the same way a newer open would tell it.
    this.#loadingSession += 1;

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDrawer);
}
